import { useState, useEffect } from 'react'
import type { ContactSubmission } from '@/types'
import { getContactSubmissions, deleteContactSubmission } from '@/utils/api'
import ConfirmModal from '@/admin/ConfirmModal'

function ContactSubmissions() {
  const [submissions, setSubmissions] = useState<ContactSubmission[]>([])
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedSubmission, setSelectedSubmission] = useState<ContactSubmission | null>(null)
  const [deletingId, setDeletingId] = useState<number | null>(null)
  const [deleteTargetId, setDeleteTargetId] = useState<number | null>(null)

  const loadSubmissions = async (pageNum: number) => {
    setLoading(true)
    setError(null)
    try {
      const result = await getContactSubmissions(pageNum, 20)
      setSubmissions(result.items || [])
      setTotalPages(Math.max(1, Math.ceil((result.total || 0) / (result.limit || 20))))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar envíos')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadSubmissions(page)
  }, [page])

  const confirmDelete = (id: number) => {
    setDeleteTargetId(id)
  }

  const handleDelete = async () => {
    if (!deleteTargetId) return

    setDeletingId(deleteTargetId)
    try {
      await deleteContactSubmission(deleteTargetId)
      setSubmissions((prev) => prev.filter((s) => s.id !== deleteTargetId))
      if (selectedSubmission?.id === deleteTargetId) {
        setSelectedSubmission(null)
      }
      setDeleteTargetId(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al eliminar envío')
    } finally {
      setDeletingId(null)
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('es-AR', {
      dateStyle: 'medium',
      timeStyle: 'short',
    })
  }

  return (
    <div className="min-h-screen">
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-text-primary mb-2">Envíos de Contacto</h1>
          <p className="text-text-secondary">
            {loading ? 'Cargando...' : `${submissions.length} envíos en esta página`}
          </p>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-error/10 text-error rounded">
          {error}
        </div>
      )}

      {loading ? (
        <div className="glass-card rounded-lg p-8 text-center">
          <i className="ph ph-spinner animate-spin text-2xl text-primary mb-4"></i>
          <p className="text-text-secondary">Cargando envíos...</p>
        </div>
      ) : submissions.length === 0 ? (
        <div className="glass-card rounded-lg p-8 text-center">
          <i className="ph ph-inbox text-4xl text-gray-300 mb-4"></i>
          <p className="text-text-secondary">No hay envíos de contacto recibidos aún</p>
        </div>
      ) : (
        <div className="glass-card rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-primary/5">
                <tr>
                  <th className="px-4 py-2 text-left text-sm font-medium text-text-primary">Nombre</th>
                  <th className="px-4 py-2 text-left text-sm font-medium text-text-primary">Email</th>
                  <th className="px-4 py-2 text-left text-sm font-medium text-text-primary">Mascota</th>
                  <th className="px-4 py-2 text-left text-sm font-medium text-text-primary">Especie</th>
                  <th className="px-4 py-2 text-left text-sm font-medium text-text-primary">Tipo</th>
                  <th className="px-4 py-2 text-left text-sm font-medium text-text-primary">Fecha</th>
                  <th className="px-4 py-2 text-right text-sm font-medium text-text-primary">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {submissions.map((submission) => (
                  <tr key={submission.id} className="border-t border-gray-200 hover:bg-gray-50">
                    <td className="px-4 py-2 text-sm text-text-primary">{submission.full_name}</td>
                    <td className="px-4 py-2 text-sm text-text-primary">{submission.email}</td>
                    <td className="px-4 py-2 text-sm text-text-primary">{submission.pet_name}</td>
                    <td className="px-4 py-2 text-sm text-text-primary">{submission.animal_species}</td>
                    <td className="px-4 py-2 text-sm text-text-primary">{submission.consultation_type}</td>
                    <td className="px-4 py-2 text-sm text-text-secondary">{formatDate(submission.submitted_at)}</td>
                    <td className="px-4 py-2 text-right space-x-2">
                      <button
                        onClick={() => setSelectedSubmission(submission)}
                        className="text-primary hover:text-primary-dark text-sm font-medium"
                      >
                        Ver detalle
                      </button>
                      <button
                        onClick={() => confirmDelete(submission.id)}
                        disabled={deletingId === submission.id}
                        className="text-red-600 hover:text-red-800 text-sm font-medium disabled:opacity-50"
                      >
                        {deletingId === submission.id ? 'Eliminando...' : 'Eliminar'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="px-3 py-1.5 rounded-lg border border-gray-300 text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Anterior
            </button>
            <span className="text-sm text-text-secondary">Página {page} de {totalPages}</span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              className="px-3 py-1.5 rounded-lg border border-gray-300 text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Siguiente
            </button>
          </div>
        </div>
      )}

      {selectedSubmission && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-lg font-semibold text-text-primary">Detalle del envío</h2>
              <button
                onClick={() => setSelectedSubmission(null)}
                className="text-text-secondary hover:text-text-primary"
              >
                <i className="ph ph-x text-xl"></i>
              </button>
            </div>
            <div className="p-6 space-y-3 text-sm">
              <div>
                <span className="font-medium text-text-primary">Nombre:</span>
                <span className="ml-2 text-text-secondary">{selectedSubmission.full_name}</span>
              </div>
              <div>
                <span className="font-medium text-text-primary">Email:</span>
                <span className="ml-2 text-text-secondary">{selectedSubmission.email}</span>
              </div>
              {selectedSubmission.phone && (
                <div>
                  <span className="font-medium text-text-primary">Teléfono:</span>
                  <span className="ml-2 text-text-secondary">{selectedSubmission.phone}</span>
                </div>
              )}
              <div>
                <span className="font-medium text-text-primary">Mascota:</span>
                <span className="ml-2 text-text-secondary">{selectedSubmission.pet_name}</span>
              </div>
              <div>
                <span className="font-medium text-text-primary">Especie:</span>
                <span className="ml-2 text-text-secondary">{selectedSubmission.animal_species}</span>
              </div>
              {selectedSubmission.pet_age && (
                <div>
                  <span className="font-medium text-text-primary">Edad:</span>
                  <span className="ml-2 text-text-secondary">{selectedSubmission.pet_age}</span>
                </div>
              )}
              <div>
                <span className="font-medium text-text-primary">Tipo de consulta:</span>
                <span className="ml-2 text-text-secondary">{selectedSubmission.consultation_type}</span>
              </div>
              <div>
                <span className="font-medium text-text-primary">Descripción:</span>
                <p className="mt-1 text-text-secondary whitespace-pre-wrap bg-gray-50 p-3 rounded">
                  {selectedSubmission.description}
                </p>
              </div>
              <div>
                <span className="font-medium text-text-primary">Fecha:</span>
                <span className="ml-2 text-text-secondary">{formatDate(selectedSubmission.submitted_at)}</span>
              </div>
            </div>
              <div className="p-6 border-t border-gray-200 flex justify-end">
                <button
                  onClick={() => confirmDelete(selectedSubmission.id)}
                  disabled={deletingId === selectedSubmission.id}
                  className="px-4 py-2 bg-error text-white rounded-lg hover:bg-red-700 transition disabled:opacity-50"
                >
                  {deletingId === selectedSubmission.id ? 'Eliminando...' : 'Eliminar envío'}
                </button>
              </div>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={!!deleteTargetId}
        title="Eliminar envío"
        message="¿Estás seguro de que querés eliminar este envío? Esta acción no se puede deshacer."
        confirmText="Eliminar"
        cancelText="Cancelar"
        onConfirm={handleDelete}
        onCancel={() => setDeleteTargetId(null)}
        isConfirming={deletingId === deleteTargetId}
      />
    </div>
  )
}

export default ContactSubmissions